import { Configuration } from '@ata-digital/client-tax-system-api-client';
import { getActiveTokenManager } from '../auth/tokenManager';
import { handleApiError, isAuthError } from './errorHandler';
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { createLogger } from '@/utils/logger';

const log = createLogger('ApiConfig');

/**
 * Shared axios instance for all API clients
 * API client v1.0.42+ handles serialization correctly
 */
export const apiAxiosInstance = axios.create();

/**
 * Singleton refresh promise to prevent race conditions.
 * When multiple requests receive 401 simultaneously, they all await the same promise.
 * The promise is cleared only after the refresh completes (success or failure).
 */
let refreshPromise: Promise<string> | null = null;

// Request interceptor to add fresh token to all requests.
// Uses route-aware token selection:
// - Client routes (/, /documents, etc.) → clientTokenManager (sessionStorage)
// - Preparer routes (/preparer/*) → tokenManager (localStorage)
apiAxiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip token refresh for auth endpoints
    if (config.url?.includes('/api/Auth/')) {
      return config;
    }

    // Try to get a valid token from the appropriate manager (will refresh if needed)
    try {
      const activeManager = getActiveTokenManager();
      const token = await activeManager.getValidToken();
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (error) {
      log.warn('Failed to get valid token for request:', error);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor to handle auth errors with retry after refresh.
// Uses route-aware token manager for refresh and token clearing.
apiAxiosInstance.interceptors.response.use(
  (response) => {
    // Skip session extension for auth endpoints - these are login/logout/refresh operations
    // that shouldn't affect the session timeout
    if (response.config.url?.includes('/api/Auth/')) {
      return response;
    }

    // Extend session on successful authenticated requests
    const activeManager = getActiveTokenManager();
    if (activeManager.isAuthenticated()) {
      activeManager.extendSession();
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Skip token refresh for auth endpoints - these are login/logout/refresh operations
    // that don't need or can't use token refresh (user may not be authenticated yet)
    if (originalRequest?.url?.includes('/api/Auth/')) {
      return Promise.reject(error);
    }

    // Only handle auth errors if the request had an Authorization header
    const hasAuthHeader = originalRequest?.headers?.Authorization;

    // Handle authentication errors (401) for authenticated requests
    if (isAuthError(error) && hasAuthHeader && !originalRequest._retry) {
      originalRequest._retry = true;

      // Get the appropriate token manager based on current route
      const activeManager = getActiveTokenManager();

      // Atomic refresh: if no refresh is in progress, start one.
      // All concurrent 401s will await the same promise.
      if (!refreshPromise) {
        log.debug('Starting token refresh');
        refreshPromise = activeManager
          .refreshToken()
          .then((token) => {
            log.debug('Token refresh succeeded');
            return token;
          })
          .catch((refreshError) => {
            // If refresh fails, clear tokens and dispatch event
            log.warn('Token refresh failed, clearing tokens');
            activeManager.clearTokens();
            window.dispatchEvent(
              new CustomEvent('auth:token-expired', {
                detail: { error: handleApiError(refreshError) },
              })
            );
            throw refreshError;
          })
          .finally(() => {
            // Clear the promise AFTER completion so concurrent requests can await it
            refreshPromise = null;
          });
      } else {
        log.debug('Awaiting existing token refresh');
      }

      try {
        // Wait for refresh to complete (all concurrent 401s await the same promise)
        const newToken = await refreshPromise!;

        // Update the failed request with new token and retry
        if (originalRequest.headers && newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        return apiAxiosInstance(originalRequest);
      } catch (_refreshError) {
        // Refresh failed, reject the original request
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  }
);

/**
 * Creates API configuration with optional authentication token.
 * Uses route-aware token manager to get the appropriate token.
 *
 * @param token - Optional token to use:
 *   - `undefined` (or omitted): Use stored token from active token manager
 *   - `''` (empty string): Explicitly no authentication (for login/public endpoints)
 *   - `'<token>'`: Use the provided token
 */
export const createApiConfig = (token?: string) => {
  const config = new Configuration({
    basePath: import.meta.env.VITE_API_URL || 'http://localhost:8080',
    accessToken:
      // Use explicit check for undefined to allow empty string to mean "no auth"
      token !== undefined
        ? token
        : () => {
            // Dynamic token retrieval using route-aware token manager
            const activeManager = getActiveTokenManager();
            return activeManager.getStoredToken() || '';
          },
    baseOptions: {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  });

  return config;
};

/**
 * Creates API configuration with automatic token refresh.
 * Uses route-aware token manager to get and refresh the appropriate token.
 * Note: This configuration doesn't include axios instance -
 * it should be passed when creating API clients
 */
export const createApiConfigWithRefresh = () =>
  new Configuration({
    basePath: import.meta.env.VITE_API_URL || 'http://localhost:8080',
    accessToken: async () => {
      // Get the appropriate token manager based on current route
      const activeManager = getActiveTokenManager();

      // Automatically refresh token if needed
      try {
        const validToken = await activeManager.getValidToken();
        if (!validToken) {
          // If no valid token, trigger redirect to login
          window.dispatchEvent(new CustomEvent('auth:redirect-to-login'));
        }
        return validToken || '';
      } catch (_error) {
        // Trigger redirect to login on token refresh failure
        window.dispatchEvent(new CustomEvent('auth:redirect-to-login'));
        return '';
      }
    },
    baseOptions: {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  });

/**
 * Default API configuration without authentication
 */
export const defaultApiConfig = createApiConfig();
